import { isHtmlElement } from '../utils';
import { getMillisecondsModifierValue } from '../utils/modifiers';
/**
 * Turns a popover panel into a self-dismissing notification.
 *
 * - x-notification - The root element. Binds role="alert" plus x-popover and
 *   x-popover:panel.nofloat on the same element, so the notification is its own
 *   popover panel. Open it with $popover.open().
 *
 * Root modifier:
 * - .duration.5000ms - How long the notification stays open before it dismisses itself (default 5000ms).
 *
 * Behavior:
 * - The dismiss timer starts when the panel expands.
 * - Pointer hover cancels the timer, and keyboard focus inside the panel cancels it too. Leaving restarts it.
 * - dismiss() cancels the timer, collapses the panel, and returns focus to the element
 *   that had it before the panel opened. Focus stays put when a popup is open.
 *
 * @example
 * <div x-notification.duration.8000ms x-init="$nextTick(() => $popover.open())">
 *   <p>Added to cart</p>
 *   <button @click="$notification.dismiss()">Close</button>
 * </div>
 */
export default function (Alpine) {
    Alpine.directive('notification', ($el, data, utilities) => {
        handleNotification($el, Alpine, data, utilities);
    }).before('bind');
    Alpine.magic('notification', handleNotificationMagic(Alpine));
}
function handleNotificationMagic(Alpine) {
    return function ($el) {
        if (!isHtmlElement($el)) {
            throw new Error(`Expected HTMLElement but got ${$el}`);
        }
        const $data = Alpine.$data($el);
        return {
            dismiss: $data.__notification_dismiss.bind($data),
            startTimer: $data.__notification_startTimer.bind($data),
            cancelTimer: $data.__notification_cancelTimer.bind($data),
            get visible() {
                return $data.__notification_visible;
            },
        };
    };
}
function handleNotification($el, Alpine, { modifiers }, _utilities) {
    var _a;
    const duration = (_a = getMillisecondsModifierValue(modifiers, 'duration')) !== null && _a !== void 0 ? _a : 5000;
    Alpine.bind($el, {
        ['x-data']() {
            return {
                __notification_intervalId: undefined,
                __notification_duration: duration,
                __notification_previousActiveElement: null,
                init() {
                    this.$nextTick(() => {
                        this.$watch('$disclosure.isExpanded', (expanded, wasExpanded) => {
                            if (expanded && !wasExpanded) {
                                this.__notification_previousActiveElement = document.activeElement;
                                if (this.__notification_duration !== Infinity) {
                                    this.__notification_startTimer();
                                }
                            }
                        });
                    });
                },
                destroy() {
                    this.__notification_cancelTimer();
                },
                __notification_cancelTimer() {
                    clearTimeout(this.__notification_intervalId);
                    this.__notification_intervalId = undefined;
                },
                __notification_startTimer() {
                    this.__notification_cancelTimer();
                    this.__notification_intervalId = setTimeout(() => {
                        this.__notification_dismiss();
                    }, this.__notification_duration);
                },
                get __notification_visible() {
                    return this.$disclosure.isExpanded;
                },
                __notification_dismiss() {
                    this.__notification_cancelTimer();
                    this.$disclosure.collapse();
                    if (this.__notification_previousActiveElement) {
                        this.$nextTick(() => {
                            var _a;
                            if (this.$popup.isOpen)
                                return;
                            (_a = this.__notification_previousActiveElement) === null || _a === void 0 ? void 0 : _a.focus();
                            this.__notification_previousActiveElement = null;
                        });
                    }
                },
            };
        },
        role: 'alert',
        'x-popover': '',
        'x-popover:panel.nofloat': '',
        '@focusin': '$isKeyboardActive && $notification.cancelTimer()',
        '@focusout': '$disclosure.isExpanded && $notification.startTimer()',
        'x-hover:start': '$notification.cancelTimer()',
        'x-hover:end': '$disclosure.isExpanded && $notification.startTimer()',
    });
    return {};
}
